'use client';

export interface PWAPlatformInfo {
  isIOS: boolean;
  isAndroid: boolean;
  isChrome: boolean;
  isSafari: boolean;
  canInstallPWA: boolean;
  requiresManualInstall: boolean;
  platform: 'ios' | 'android' | 'desktop';
}

export const getPWAPlatform = (): PWAPlatformInfo => {
  if (typeof window === 'undefined') {
    return {
      isIOS: false,
      isAndroid: false,
      isChrome: false,
      isSafari: false,
      canInstallPWA: false,
      requiresManualInstall: false,
      platform: 'desktop',
    };
  }

  const ua = navigator.userAgent;
  const isIOS = /iPhone|iPad|iPod/.test(ua) && !(window as any).MSStream;
  const isAndroid = /Android/.test(ua);
  const isChrome = /Chrome/.test(ua) && /Google Inc/.test(navigator.vendor || '');
  const isSafari = /Safari/.test(ua) && /Apple Computer/.test(navigator.vendor || '') && !isChrome;

  let platform: 'ios' | 'android' | 'desktop' = 'desktop';
  if (isIOS) platform = 'ios';
  else if (isAndroid) platform = 'android';

  return {
    isIOS,
    isAndroid,
    isChrome,
    isSafari,
    canInstallPWA: false, 
    requiresManualInstall: isIOS && isSafari,
    platform,
  };
};

export const isPWAInstalled = (): boolean => {
  if (typeof window === 'undefined') return false;

  return (
    window.matchMedia('(display-mode: standalone)').matches ||
    (window.navigator as any).standalone ||
    document.referrer.includes('android-app://')
  );
};

export const canShowInstallPrompt = (isMobile: boolean): boolean => {
  const platform = getPWAPlatform();

  // Only show on iOS Safari
  if (!platform.isIOS || !platform.isSafari) {
    console.log('[PWA Debug] Not showing banner: not iOS Safari');
    return false;
  }

  if (isPWAInstalled()) {
    console.log('[PWA Debug] Not showing banner: PWA already installed');
    return false;
  }

  // Check if banner was dismissed in this session
  const dismissed = sessionStorage.getItem('pwa-banner-dismissed');
  if (dismissed) {
    console.log('[PWA Debug] Banner already dismissed in this session');
    return false;
  }

  console.log('[PWA Debug] Banner can show on iOS Safari');
  return true;
};

export const dismissInstallPrompt = (): void => {
  sessionStorage.setItem('pwa-banner-dismissed', 'true');
  console.log(`[PWA Debug] Banner dismissed for this session`);
};

export const getInstallInstructions = (platform: 'ios' | 'android' | 'desktop'): string[] => {
  // Only iOS Safari is supported now
  return [
    'Tap the Share button at the bottom',
    'Scroll down and tap "Add to Home Screen"',
    'Tap "Add" in the top right corner'
  ];
};
